import React, { useState, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Input } from '@mellify/ui';
import { PlatformIcon } from './PlatformIcon';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { apiClient } from '../../../../services/api/client';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { Key, ExternalLink, AlertCircle, CheckCircle2 } from 'lucide-react';

interface ConnectDailyDevModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ConnectDailyDevModal: React.FC<ConnectDailyDevModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const activeWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  const connectMutation = useMutation({
    mutationFn: async (key: string) => {
      const res = await apiClient.post(
        `/social-accounts/dailydev/connect`,
        {
          apiKey: key,
          workspaceId: activeWorkspace?.id,
        }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
      setApiKey('');
      setError(null);
      onSuccess();
    },
    onError: (err: any) => {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to connect daily.dev account. Please check your API key and try again.';
      setError(message);
    },
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      const trimmedKey = apiKey.trim();
      if (!trimmedKey) {
        setError('Please enter your daily.dev API key.');
        return;
      }

      connectMutation.mutate(trimmedKey);
    },
    [apiKey, connectMutation]
  );

  const handleClose = useCallback(() => {
    if (!connectMutation.isPending) {
      setApiKey('');
      setError(null);
      onClose();
    }
  }, [connectMutation.isPending, onClose]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="sm"
      aria-labelledby="connect-dailydev-title"
    >
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-dark-700 border border-dark-200 dark:border-dark-600">
            <PlatformIcon platform="dailydev" size="md" />
          </div>
          <div>
            <h2
              id="connect-dailydev-title"
              className="text-lg font-semibold text-dark-900 dark:text-dark-100"
            >
              Connect daily.dev
            </h2>
            <p className="text-xs text-dark-500 dark:text-dark-400">
              Authenticate with your API key
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-lg bg-dark-50 dark:bg-dark-800 p-3 text-xs text-dark-600 dark:text-dark-300 space-y-2">
            <p className="font-medium flex items-center gap-1.5">
              <Key className="w-3.5 h-3.5" />
              How to get your API key:
            </p>
            <ol className="list-decimal list-inside space-y-1 ml-1">
              <li>Go to your daily.dev account settings</li>
              <li>Navigate to the &quot;Security&quot; or &quot;API&quot; section</li>
              <li>Generate a new personal access token</li>
              <li>Copy the token and paste it below</li>
            </ol>
            <a
              href="https://app.daily.dev/account/security"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline font-medium mt-1"
            >
              Open daily.dev settings
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <div>
            <label
              htmlFor="dailydev-api-key"
              className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1.5"
            >
              API Key
            </label>
            <Input
              id="dailydev-api-key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your daily.dev API key"
              disabled={connectMutation.isPending}
              autoComplete="off"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-xs text-red-700 dark:text-red-400">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {connectMutation.isSuccess && (
            <div className="flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-900/20 p-3 text-xs text-green-700 dark:text-green-400">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>daily.dev account connected successfully!</span>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={connectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={connectMutation.isPending || !apiKey.trim()}
              loading={connectMutation.isPending}
            >
              Connect Account
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

ConnectDailyDevModal.displayName = 'ConnectDailyDevModal';